import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { CareArea } from 'interfaces/care-area';
import { Facility } from 'interfaces/facility';
import FacilityService from 'services/FacilityService';
import { toast } from 'react-hot-toast';
import Spinner from 'components/UI/Spinner/Spinner';
import { Drug } from 'interfaces/drug';
import SwalAlert, {
  firePreConfirmAlert,
} from 'components/UI/SwalAlert/SwalAlert';
import DrugService from 'services/DrugService';
import { SweetAlertResult } from 'sweetalert2';
import { ScreenEnum } from 'pages/Device/enums';

/**
 * Props for the CopyDrugCareAreas component
 * @typedef {Object} CopyDrugCareAreasProps
 * @property {Drug} drug - The drug to be copied
 * @property {Facility} facility - The facility containing care areas
 * @property {boolean} canManage - Whether the user has permission to manage drugs
 * @property {Function} setScreen - Function to change the current screen
 */
type CopyDrugCareAreasProps = {
  drug: Drug;
  facility: Facility;
  canManage: boolean;
  setScreen: Function;
};

/**
 * @description Component that allows copying a drug to different care areas within a facility.
 * Displays available care areas and handles the drug copy operation with confirmation.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {CopyDrugCareAreasProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const CopyDrugCareAreas: FC<CopyDrugCareAreasProps> = ({
  drug,
  facility,
  setScreen,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: careAreas, isLoading: isLoadingCareAreas } = useQuery({
    queryKey: [
      ReactQueryKeys.FACILITY_CARE_AREAS,
      { facilityId: facility.id, drugId: drug.id },
    ],

    queryFn: () =>
      FacilityService.getCareAreas(
        String(facility.id),
        String(drug.list_drug_id),
      ),

    placeholderData: keepPreviousData,

    // @ts-ignore
    enabled: !!facility && !isNaN(facility.id),
  });

  /**
   * @description Copies drug to a selected care area after confirmation.
   * Shows confirmation dialog and success toast on completion.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {CareArea} careArea - Care area that the drug will be copied to
   *
   * @returns {Promise<void>}
   */
  const handleCareAreaClick = async (careArea: CareArea) => {
    firePreConfirmAlert({
      focusCancelBtn: false,
      title: t('device.messages.modal__copy_drug__title'),
      html: t('device.messages.modal__copy_drug__content', {
        drugName: drug.name,
        careAreaName: careArea.name,
      }),
      preConfirm: () => {
        return DrugService.copyDrugToCareArea(
          String(drug.id),
          String(careArea.id),
          String(facility.id),
        )
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.FACILITY_CARE_AREAS],
        });
        toast.success(t('device.messages.copy_drug__success'));
      }
    });
  };

  /**
   * @description Cancels the drug copy operation and returns to the overview screen.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {void}
   */
  const handleCancelCopyDrug = () => {
    setScreen(ScreenEnum.Overview);
  };

  return (
    <>
      {/* <pre>{JSON.stringify(drug)}</pre> */}
      <h6 className="device__care-area--title mt-3 pb-1">
        {t('device.messages.copy_drug_to_care_area', {
          name: facility.name,
        })}
      </h6>

      <div className="row" data-testid="careareas-screen">
        <div className="col-6">
          {isLoadingCareAreas ? <Spinner isAbsolute /> : null}

          <div></div>
          {careAreas?.data?.records && careAreas.data.records.length === 0 && (
            <div className="alert mb-0">{t('messages.no_records')}</div>
          )}

          {careAreas?.data?.records && careAreas.data.records.length > 0 && (
            <div className="master-list device__care-area--full-list">
              {careAreas.data.records.map((careArea: CareArea) => {
                return (
                  <a
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCareAreaClick(careArea);
                    }}
                    className={`master-list__item device-item ${
                      careArea.hasAssignedDrug ? 'item-disabled' : ''
                    }`}
                    key={careArea.id}
                  >
                    {careArea.name}
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div className="col-6">
          <div>
            <button
              className="btn btn-lg rounded btn-secondary"
              onClick={() => handleCancelCopyDrug()}
              data-testid="cancel-copy-drug"
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CopyDrugCareAreas;
